/**
 * RecurrenceOptionsEditor — Editor de opções de periodicidade
 *
 * Reutilizado pelos diálogos de Plano (`WlPlans`) e Add-on (`WlAddons`) no
 * painel /wl. Permite vincular um serviço recorrente do tenant master a cada
 * periodicidade suportada (Mensal, Bimestral, Trimestral, Semestral, Anual)
 * com preço, taxa de setup e desconto opcional.
 *
 * Validações:
 *  - Backend (trigger) garante que recurrence_interval do serviço master
 *    bate com a periodicidade da linha.
 *  - Frontend filtra o <Select> por periodicidade para evitar erro.
 *
 * Visibilidade: deve ser renderizado apenas quando o plano/add-on usa
 * `billing_integration = 'integracao_v1'` (gateway uaizy).
 */
import { useMemo, useState } from "react";
import { Loader2, Save, Trash2 } from "lucide-react";
import {
  useWlMasterRecurringServices,
  useWlPlanRecurrenceOptions,
  useWlPlanRecurrenceMutations,
  useWlAddonRecurrenceOptions,
  useWlAddonRecurrenceMutations,
  type RecurrencePeriod,
  type WlRecurrenceOption,
} from "@/hooks/wl/useWlRecurrenceOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { showToast } from "@/lib/toast";
import { applyCurrencyMask, parseCurrency } from "@/lib/input-masks";

const PERIODS: { value: RecurrencePeriod; label: string }[] = [
  { value: "mensal", label: "Mensal" },
  { value: "bimestral", label: "Bimestral" },
  { value: "trimestral", label: "Trimestral" },
  { value: "semestral", label: "Semestral" },
  { value: "anual", label: "Anual" },
];

interface CommonProps {
  /** Plano OU add-on. */
  entity: "plan" | "addon";
  /** ID do plano (string) ou do add-on (uuid). */
  entityId: string;
}

export function RecurrenceOptionsEditor(props: CommonProps) {
  const { entity, entityId } = props;

  const planQ = useWlPlanRecurrenceOptions(entity === "plan" ? entityId : null);
  const addonQ = useWlAddonRecurrenceOptions(entity === "addon" ? entityId : null);
  const planM = useWlPlanRecurrenceMutations(entity === "plan" ? entityId : null);
  const addonM = useWlAddonRecurrenceMutations(entity === "addon" ? entityId : null);

  const list = entity === "plan" ? planQ.data : addonQ.data;
  const isLoading = entity === "plan" ? planQ.isLoading : addonQ.isLoading;

  const byPeriod = useMemo(() => {
    const m = new Map<RecurrencePeriod, WlRecurrenceOption>();
    (list || []).forEach((o) => m.set(o.recurrence_interval, o));
    return m;
  }, [list]);

  return (
    <div className="space-y-3 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-2">
        <div>
          <Label className="text-sm font-semibold">Periodicidades disponíveis</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Para cada periodicidade, vincule um serviço recorrente do tenant master
            (mesma periodicidade) e defina o preço de cobrança. Para habilitar uma
            periodicidade, selecione o serviço, defina o preço e clique em <b>Salvar</b>.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Carregando…
        </div>
      ) : (
        <div className="space-y-2">
          {PERIODS.map((p) => (
            <PeriodRow
              key={p.value}
              period={p.value}
              periodLabel={p.label}
              current={byPeriod.get(p.value) || null}
              onSave={async (payload) => {
                try {
                  const base =
                    entity === "plan"
                      ? { plan_id: entityId }
                      : { addon_id: entityId };
                  const id = byPeriod.get(p.value)?.id || null;
                  const fn = entity === "plan" ? planM.upsert : addonM.upsert;
                  await fn.mutateAsync({
                    id,
                    payload: {
                      ...base,
                      recurrence_interval: p.value,
                      ...payload,
                    },
                  });
                  showToast.success(`Periodicidade ${p.label} salva`);
                } catch (e: any) {
                  showToast.error(e?.message || "Falha ao salvar periodicidade");
                }
              }}
              onToggleActive={async (active) => {
                const opt = byPeriod.get(p.value);
                if (!opt) return;
                const fn =
                  entity === "plan" ? planM.toggleActive : addonM.toggleActive;
                await fn.mutateAsync({ id: opt.id, active });
              }}
              onRemove={async () => {
                const opt = byPeriod.get(p.value);
                if (!opt) return;
                if (!confirm(`Remover periodicidade ${p.label}?`)) return;
                try {
                  const fn = entity === "plan" ? planM.remove : addonM.remove;
                  await fn.mutateAsync(opt.id);
                  showToast.success("Periodicidade removida");
                } catch (e: any) {
                  showToast.error(e?.message || "Falha ao remover");
                }
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Linha de periodicidade
// ---------------------------------------------------------------------------
interface PeriodRowProps {
  period: RecurrencePeriod;
  periodLabel: string;
  current: WlRecurrenceOption | null;
  onSave: (payload: Record<string, unknown>) => Promise<void>;
  onToggleActive: (active: boolean) => Promise<void>;
  onRemove: () => Promise<void>;
}

function PeriodRow({
  period,
  periodLabel,
  current,
  onSave,
  onToggleActive,
  onRemove,
}: PeriodRowProps) {
  const services = useWlMasterRecurringServices(period);
  const [serviceId, setServiceId] = useState<string>(
    current?.uaizy_master_service_id || "",
  );
  const [price, setPrice] = useState<number>(current?.price ?? 0);
  const [setup, setSetup] = useState<number>(current?.setup_fee ?? 0);
  const [discount, setDiscount] = useState<string>(
    current?.discount_percent !== null && current?.discount_percent !== undefined
      ? String(current.discount_percent)
      : "",
  );

  const dirty =
    !current ||
    serviceId !== current.uaizy_master_service_id ||
    price !== current.price ||
    setup !== current.setup_fee ||
    (discount === "" ? current.discount_percent !== null : Number(discount) !== current.discount_percent);

  return (
    <div className="rounded-md border p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {current ? (
            // Linha já existe no banco: Switch controla is_active (pausar/reativar).
            <Switch
              checked={current.is_active}
              onCheckedChange={(v) => onToggleActive(v)}
            />
          ) : (
            // Sem linha persistida: chip indicando que basta selecionar
            // serviço + preço e clicar em Salvar para habilitar a periodicidade.
            <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full border border-dashed text-muted-foreground">
              Não configurado
            </span>
          )}
          <span className="text-sm font-medium">{periodLabel}</span>
        </div>
        <div className="flex items-center gap-2">
          {current && (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive"
              onClick={onRemove}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          <Button
            size="sm"
            disabled={!serviceId || !dirty}
            onClick={() =>
              onSave({
                uaizy_master_service_id: serviceId,
                price,
                setup_fee: setup,
                discount_percent: discount === "" ? null : Number(discount),
                is_active: current?.is_active ?? true,
              })
            }
          >
            <Save className="h-4 w-4 mr-1" /> Salvar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
        <div className="md:col-span-6 space-y-1">
          <Label className="text-xs">Serviço recorrente do master</Label>
          <Select value={serviceId} onValueChange={setServiceId}>
            <SelectTrigger>
              <SelectValue
                placeholder={
                  services.isLoading
                    ? "Carregando…"
                    : services.data && services.data.length > 0
                      ? "Selecionar serviço"
                      : `Nenhum serviço ${periodLabel.toLowerCase()} no master`
                }
              />
            </SelectTrigger>
            <SelectContent>
              {(services.data || []).map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                  {s.recurring_price
                    ? ` — R$ ${Number(s.recurring_price).toFixed(2)}`
                    : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-3 space-y-1">
          <Label className="text-xs">Preço por ciclo</Label>
          <Input
            placeholder="R$ 0,00"
            value={price > 0 ? applyCurrencyMask(price) : ""}
            onChange={(e) => setPrice(parseCurrency(e.target.value))}
          />
        </div>
        <div className="md:col-span-2 space-y-1">
          <Label className="text-xs">Setup</Label>
          <Input
            placeholder="R$ 0,00"
            value={setup > 0 ? applyCurrencyMask(setup) : ""}
            onChange={(e) => setSetup(parseCurrency(e.target.value))}
          />
        </div>
        <div className="md:col-span-1 space-y-1">
          <Label className="text-xs">Desc %</Label>
          <Input
            type="number"
            min={0}
            max={100}
            placeholder="0"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}
